import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { required, maxLength, between, helpers, minValue } from '@vuelidate/validators'
import validationMixinGeneral from '@apps/order-form/src/mixins/validationMixinGeneral'
import useAdaptive from '@hooks/useAdaptive'
import { GENERAL_STORE } from '../store'

export default {
  mixins: [validationMixinGeneral],

  props: {
    dot: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    requiredMinPrice: 100,
    showWeightInput: false,
    showErrors: true,
    addedServicesByForm: [],
    adaptive: useAdaptive(),
  }),

  validations() {
    return {
      name: {
        required: helpers.withMessage('Необходимо указать наименование товара', required),
        maxLength: this.base.isAdmin
          ? () => true
          : helpers.withMessage(
              ({ $params, $model }) => `Максимальная длина имени товара превышена на
        ${$model.length - $params.max} символов`,
              maxLength(60)
            ),
      },
      count: {
        required: helpers.withMessage('Необходимо указать количество', required),
        between: helpers.withMessage(
          ({ $params, $model }) =>
            `${
              $model > $params.max
                ? `Максимальное количество товара: ${$params.max}`
                : `Минимальное количество товара ${$params.min}`
            }`,
          between(1, 100)
        ),
      },
      weight: {
        required: helpers.withMessage('Необходимо указать массу единицы товара', required),
        // between: helpers.withMessage(({ $params, $model }) => {
        //   const minError = `Минимальная масса товара: ${$params.min} гр.`
        //   const maxError = 'Слишком большая масса для данного типа доставки.'
        //
        //   return $model > $params.max ? maxError : minError
        // }, between(50, this.availableWeightValues.at(-1))),
        minValue: helpers.withMessage('Минимальная масса товара: 50 гр.', minValue(50)),
        /* без сообщений - они отображается в ItemsListComponent */
        // суммарный вес товаров больше допустимого для данного типа доставки
        maxValue: () => this.totalItemsWeight <= this.availableWeightValues.at(-1),
        // Суммарный вес товаров >= максимальному весу в принципе
        maxTotalWeight: () => this.totalItemsWeight <= this.maxWeight,
      },
      price: {
        required: helpers.withMessage('Необходимо указать стоимость', required),
        between: helpers.withMessage(({ $params, $model }) => {
          const minError = this.isBuyout
            ? `Стоимость выкупаемого товара не может ровняться ${$model}.`
            : `Указывайте реальную ценность груза (выручки). Именно эта сумма компенсируется в случае утери отправления (до ${this.maxItemsCost} руб).`

          const maxError = `Максимальная сумма ${$params.max}.`

          return $model > $params.max ? maxError : minError
        }, between(this.minItemsSumCost, this.maxItemCost)),
        maxValue: () => this.totalItemsPrice <= this.maxItemsCost,
      },
    }
  },

  watch: {
    name: {
      handler(newValue) {
        if (newValue === '') {
          this.deleteServices()
        }
      },
      immediate: false,
    },
    count() {
      this.calculatePrice()
    },
    weight() {
      this.calculatePrice()
    },
    price() {
      this.calculatePrice()
    },
  },

  computed: {
    ...mapState(GENERAL_STORE, ['user', 'base', 'form', 'order']),
    ...mapGetters(GENERAL_STORE, [
      'isBuyout',
      'formSize',
      'availableWeightValues',
      'isExceededBuyoutCost',
      'maxItemsCost',
      'totalItemsPrice',
      'totalItemsWeight',
      'minItemsSumCost',
      'maxWeight',
      'maxItemCost',
      'managedServices',
    ]),

    isMobile() {
      return this.adaptive.is.lg
    },
    orderBuyoutLimit() {
      return this.isBuyout ? this.form.orderBuyoutLimit : Infinity
    },
    name: {
      get() {
        return this.item.name
      },
      set(value) {
        this.updateItem({
          item: this.item,
          data: { key: 'name', value },
        })
      },
    },

    count: {
      get() {
        return this.item.count
      },
      set(value) {
        this.updateItem({
          item: this.item,
          data: { key: 'count', value },
        })
        this.calculatePrice()
      },
    },

    weight: {
      get() {
        return this.item.weight
      },
      set(value) {
        if (value === 'x') {
          this.showWeightInput = true
          this.$nextTick(() => {
            this.$refs.weightField.focus()
          })
          return
        }

        this.updateItem({
          item: this.item,
          data: { key: 'weight', value },
        })
        this.calculatePrice()
      },
    },

    price: {
      get() {
        return this.item.price
      },
      set(value) {
        this.updateItem({
          item: this.item,
          data: { key: 'price', value },
        })
        this.calculatePrice()
      },
    },

    costTooltipMessage() {
      if (this.isBuyout) {
        return 'Сумма выкупа и страховки отправления. Для страховки воспользуйтесь услугой "Доставка без договора."'
      }

      if (this.user.isLegalEntity) {
        return 'Сумма страховки отправления.<br>При необходимости эту же сумму курьер получит у получателя и переведет вам.'
      }

      return 'Сумма страховки отправления.<br>При необходимости эту же сумму курьер примет у получателя и переведет вам.'
    },

    customAvailableWeighValues() {
      const weights = this.availableWeightValues.filter(value => !!value)

      if (!this.availableWeightValues.find(value => +this.weight === +value) && this.weight) {
        weights.push(+this.weight)
      }

      return weights
    },

    // Все действия подсказок описаны в виде массива (функции) actions.
    suggestions() {
      const suggestions = this.isBuyout
        ? [
            {
              name: 'Продукты',
              mobile_view: true,
              actions: ['updateName'],
            },
            {
              name: 'Одежда',
              mobile_view: true,
              actions: ['updateName'],
            },
            {
              name: 'Запчасти',
              mobile_view: true,
              actions: ['updateName'],
            },
            {
              name: 'Лекарства',
              mobile_view: true,
              actions: ['updateName'],
            },
            {
              name: 'Другое',
              actions: ['writeOtherItem'],
            },
          ]
        : [
            {
              name: 'Документы',
              mobile_view: true,
              actions: ['updateName'],
            },
            {
              name: `🎁 Сюрприз ${this.getServicePriceHTML(47)}`,
              mobile_view: true,
              input_name: 'Сюрприз',
              actions: ['updateName', 'addServices'],
              description:
                'Курьер выполнит доставку-сюрприз, не рассказывая получателю о содержимом посылки.',
              services: [47],
            },
            {
              name: `🌷 Хрупкий груз ${this.getServicePriceHTML(26)}`,
              input_name: 'Хрупкий груз',
              mobile_view: true,
              actions: ['updateName', 'addServices'],
              services: [26],
            },
            {
              name: `Груз >100 см ${this.getServicePriceHTML(2)}`,
              input_name: 'Груз >100 см',
              mobile_view: true,
              actions: ['updateName', 'addServices', 'addDetails'],
              services: [2],
            },
            {
              name: 'Другое',
              actions: ['writeOtherItem'],
            },
          ]

      const lastItem = this.form.lastItemName

      if (!lastItem) return suggestions

      const includesLastItem = suggestions.find(
        suggestion => suggestion.input_name === lastItem || suggestion.name === lastItem
      )

      if (includesLastItem) return suggestions

      return [{ name: lastItem, mobile_view: true, actions: ['updateName'] }, ...suggestions]
    },
    isEnoughWidth() {
      return this.formSize === 0
    },
    suggestionValues() {
      return this.suggestions.filter(
        item => (this.isEnoughWidth && item.mobile_view) || !this.isEnoughWidth
      )
    },
    computedPlaceholder() {
      if (this.isBuyout) {
        return 'Что покупаем'
      }

      return 'Что везем'
    },
    isMinTotalPriceMatched() {
      return this.item.count * this.item.price >= this.requiredMinPrice
    },
    namePlaceholder() {
      return `Что ${this.isBuyout ? 'выкупаем' : 'везем'}`
    },
    itemPricePlaceholder() {
      return `${this.isBuyout ? 'Сумма выкупа' : 'Ценность'} за шт.`
    },
    itemServices() {
      return this.item.services
    },
  },

  methods: {
    ...mapMutations(GENERAL_STORE, [
      'updateItem',
      'turnOnServices',
      'turnOffServices',
      'removeDotItem',
      'addDotItem',
    ]),
    ...mapActions(GENERAL_STORE, ['calculatePrice']),

    getServicePrice(serviceId) {
      return this.managedServices.find(serviceItem => serviceItem.id === serviceId)?.price
    },

    /**
     * @param {number | number[]} serviceId
     * @returns {string}
     */
    getServicePriceHTML(serviceId) {
      const price = Array.isArray(serviceId)
        ? serviceId.reduce((prev, id) => prev + this.getServicePrice(id), 0)
        : this.getServicePrice(serviceId)

      if (!price) {
        console.error('Не найден сервис с таким id', serviceId)
        return 'Ошибка. Услуга не найдена. Обратитесь в поддержку'
      }

      return `<span class="extra-price">+${price} ₽</span>`
    },

    updateName(suggestion) {
      if (suggestion.input_name) {
        this.name = suggestion.input_name
      } else if (suggestion.short_name) {
        this.name = suggestion.short_name
      } else {
        this.name = suggestion.name
      }
    },

    deleteItemFromDot({ dot, item }) {
      this.removeDotItem({ dot, item })
      this.calculatePrice()
    },

    onBlurWeight() {
      const weight = +this.weight

      if (!weight && this.showWeightInput) {
        this.showWeightInput = false
      }

      if (!weight) this.weight = 500

      this.touchField('weight')
    },

    getSuggestionName(suggestion) {
      return this.isEnoughWidth && suggestion.short_name !== undefined
        ? suggestion.short_name
        : suggestion.name
    },
    chooseSuggestion(suggestion) {
      this.deleteServices()
      suggestion.actions.forEach(action => {
        if (this[action] !== undefined) {
          this[action](suggestion)
        }
      })
    },
    writeOtherItem() {
      this.name = ''
      this.$refs.nameField.focus()
    },
    addDetails() {
      this.name += ': '
      this.$refs.nameField.focus()
    },

    addServices(suggestion) {
      this.turnOnServices({
        item: this.item,
        services: suggestion.services,
      })
      this.calculatePrice()
    },

    deleteServices() {
      this.turnOffServices(this.item)
      this.calculatePrice()
    },

    clearName() {
      this.name = ''
    },

    inputPriceHandler() {
      if (this.price > this.maxItemCost) this.price = this.maxItemCost
      this.touchField('price')
    },
    changeCountHandler() {
      this.touchField('count')
      this.touchField('weight')
    },
  },

  created() {
    if (
      this.item.weight !== 1000 &&
      !this.availableWeightValues.find(val => val === this.item.weight)
    ) {
      this.showWeightInput = true
    }
  },

  mounted() {
    this.addFieldValidator('name', this.$refs.nameField)
    this.addFieldValidator('price', this.$refs.priceField)
    this.addFieldValidator('count', this.$refs.countField)
    this.addFieldValidator('weight', this.$refs.weightFieldContainer)
  },
}
